import { Fragment, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Home from "./site/Home";
import Catalogue from "./site/Catalogue";
import Landing from "./site/Landing";
import ThankYou from "./site/ThankYou";
import NotFound from "./site/NotFound";
import { trackPageView } from "./lib/pixel";
import { I18nProvider, LANGS } from "./i18n";

// The admin is code-split so it doesn't add weight to the public site.
const AdminApp = lazy(() => import("./admin/AdminApp"));

function RouteEffects() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!pathname.startsWith("/admin")) trackPageView(pathname);
  }, [pathname]);

  // Scroll to a section (#kontakt) even after navigating from another page, while content is still rendering.
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    // Wait until the target exists and the mobile menu has released its scroll lock
    // (body overflow: hidden cancels an in-flight scroll), then re-align once the layout settles.
    let tries = 0;
    let settle;
    const timer = setInterval(() => {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      const locked = document.body.style.overflow === "hidden";
      if ((el && !locked) || ++tries > 40) {
        clearInterval(timer);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        settle = setTimeout(() => {
          const top = el.getBoundingClientRect().top;
          const offset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
          if (Math.abs(top - offset) > 4) el.scrollIntoView({ behavior: "auto", block: "start" });
        }, 900);
      }
    }, 50);
    return () => {
      clearInterval(timer);
      clearTimeout(settle);
    };
  }, [pathname, hash]);

  return null;
}

const LEGACY_ADMIN = "/admin/785b17a3-f6f5-42a1-8ad5-0f3ce069c4f9";

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <RouteEffects />
        <Routes>
          {/* Every public page exists in each language: /katalog, /en/katalog, /ru/katalog … */}
          {Object.values(LANGS).map(({ code, prefix }) => (
            <Fragment key={code}>
              <Route path={prefix || "/"} element={<Home />} />
              <Route path={`${prefix}/katalog`} element={<Catalogue />} />
              <Route path={`${prefix}/katalog/:slug`} element={<Catalogue />} />
              <Route path={`${prefix}/catalogue`} element={<Navigate to={`${prefix}/katalog`} replace />} />
              <Route path={`${prefix}/landing`} element={<Landing />} />
              <Route path={`${prefix}/ponuda`} element={<Landing />} />
              <Route path={`${prefix}/hvala`} element={<ThankYou />} />
              <Route path={`${prefix}/thanks`} element={<ThankYou />} />
            </Fragment>
          ))}
          <Route path={`${LEGACY_ADMIN}/*`} element={<Navigate to="/admin" replace />} />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<div className="admin-loading"><span className="spinner" /></div>}>
                <AdminApp />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </I18nProvider>
    </BrowserRouter>
  );
}
